import {
  AgenticMatch,
  extractAxiosErrorMessage,
  PromptAddendumModel,
  useLandlordResearchTrace,
  useLeads,
  useOperation,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner, Form } from "react-bootstrap";

// The result blob the landlord test-run worker writes to the operation (dryRun path).
interface TestRunResult {
  status?: string;
  candidates?: AgenticMatch[];
  webSearchCount?: number;
  traceSubjectRef?: string;
  traceRunId?: string;
}

const confidenceVariant = (c: number): string => {
  if (c >= 70) return "success";
  if (c >= 40) return "warning";
  return "secondary";
};

// Citation URLs originate from web content the agent read, so only render http(s)
// as a live link - never a javascript:/data: scheme in the admin origin.
const isSafeHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url.trim());

// The landlord-research prompt's test panel: runs the current draft against a real
// subject (VOA ref or promoted lead) as a long-running research op and renders the
// confirmed owners + citations + AI trace. Handed the live draft addendum by the
// shared PromptRuleSetEditor.
const LandlordTestPanel: React.FC<{ addendum: PromptAddendumModel }> = ({
  addendum,
}) => {
  const { testLandlordResearchPrompt } = useLeads();

  const [subjectKind, setSubjectKind] = useState<"voa" | "lead">("voa");
  const [subjectRef, setSubjectRef] = useState<string>("");
  const [testOp, setTestOp] = useState<{ key: string; siteId: string } | null>(
    null
  );
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestRunResult | null>(null);
  const [showTrace, setShowTrace] = useState<boolean>(false);

  const testOpQuery = useOperation(testOp?.key ?? null, testOp?.siteId ?? null);
  const testStatus = testOpQuery.data?.operation?.status;
  const testRunning =
    testOp !== null &&
    testStatus !== "completed" &&
    testStatus !== "failed" &&
    testStatus !== "cancelled";
  const searchesRun = testOpQuery.data?.operation?.progress?.processed ?? 0;

  useEffect(() => {
    if (!testOp || !testStatus) return;
    if (testStatus === "completed") {
      setTestResult((testOpQuery.data?.operation?.result as TestRunResult) ?? {});
      setTestOp(null);
    } else if (testStatus === "failed" || testStatus === "cancelled") {
      setTestError(
        testOpQuery.data?.operation?.error ?? "The test run did not complete."
      );
      setTestOp(null);
    }
  }, [testStatus, testOp, testOpQuery.data]);

  const handleTest = async () => {
    setTestError(null);
    setTestResult(null);
    setShowTrace(false);
    try {
      const res = await testLandlordResearchPrompt.mutateAsync({
        addendum,
        subjectRef: subjectRef.trim(),
        subjectKind,
      });
      setTestOp({ key: res.operationKey, siteId: res.siteId });
    } catch (e) {
      setTestError(
        (await extractAxiosErrorMessage(e)) ?? "Failed to start the test run."
      );
    }
  };

  const traceQuery = useLandlordResearchTrace(
    testResult?.traceSubjectRef ?? null,
    testResult?.traceRunId ?? null,
    showTrace
  );

  return (
    <div>
      <strong>Test this prompt</strong>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
        Runs the current draft (saved rules plus any unsaved preamble edit) against
        a real row for its address and context. Nothing is saved and the row is not
        changed. Costs up to ~$2 per run.
      </div>
      <div
        style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div>
          <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>Subject</Form.Label>
          <Form.Select
            size="sm"
            value={subjectKind}
            onChange={(e) => setSubjectKind(e.target.value as "voa" | "lead")}
            style={{ width: 180 }}
          >
            <option value="voa">VOA assessment ref</option>
            <option value="lead">Promoted lead id</option>
          </Form.Select>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Form.Control
            size="sm"
            value={subjectRef}
            placeholder={subjectKind === "voa" ? "e.g. 30799525000" : "lead id (UUID)"}
            onChange={(e) => setSubjectRef(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={
            testRunning || !subjectRef.trim() || testLandlordResearchPrompt.isLoading
          }
          onClick={() => void handleTest()}
        >
          {testRunning ? (
            <>
              <Spinner as="span" size="sm" animation="border" />{" "}
              {searchesRun > 0
                ? `Researching... (${searchesRun} searches run)`
                : "Researching..."}
            </>
          ) : (
            "Test run (up to ~$2)"
          )}
        </Button>
      </div>

      {testRunning && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#0a58ca" }}>
          Researching on the open web - this can take a few minutes.
        </div>
      )}
      {testError && (
        <Alert variant="danger" style={{ marginTop: 8 }}>
          {testError}
        </Alert>
      )}

      {testResult && (
        <div style={{ marginTop: 10 }}>
          {testResult.status === "not_configured" ? (
            <Alert variant="warning">
              Web search is not configured in this environment, so the test could
              not run.
            </Alert>
          ) : testResult.status &&
            !["complete", "no_answer"].includes(testResult.status) ? (
            <Alert variant="danger">
              The test run did not complete (status: {testResult.status}). The
              subject row may have changed - try again.
            </Alert>
          ) : (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
              >
                <strong>
                  {testResult.candidates?.length ?? 0} candidate
                  {(testResult.candidates?.length ?? 0) === 1 ? "" : "s"}
                </strong>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {testResult.webSearchCount ?? 0} web searches
                </span>
                {testResult.traceRunId && (
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 12, padding: 0 }}
                    onClick={() => setShowTrace(true)}
                  >
                    View AI trace
                  </Button>
                )}
              </div>
              {(testResult.candidates?.length ?? 0) === 0 ? (
                <div style={{ fontSize: 12, color: "#666" }}>
                  No owner it could confirm from public sources. Open the AI trace to
                  see what it checked.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {testResult.candidates!.map((c, i) => (
                    <div
                      key={i}
                      style={{ border: "1px solid #eee", borderRadius: 4, padding: 8 }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <strong>{c.proprietorName}</strong>
                        <Badge bg={confidenceVariant(c.confidence)}>
                          {c.confidence}
                        </Badge>
                        {c.companyNumber && (
                          <span style={{ fontSize: 11, color: "#888" }}>
                            CH {c.companyNumber}
                          </span>
                        )}
                      </div>
                      {c.reasoning && (
                        <div style={{ fontSize: 12, color: "#444", marginTop: 2 }}>
                          {c.reasoning}
                        </div>
                      )}
                      {c.citations.length > 0 && (
                        <div style={{ fontSize: 11, marginTop: 4 }}>
                          {c.citations.map((cit, ci) => (
                            <div key={ci}>
                              {isSafeHttpUrl(cit.url) ? (
                                <a
                                  href={cit.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                >
                                  {cit.title || cit.url}
                                </a>
                              ) : (
                                <span>{cit.title || cit.url}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Modal show={showTrace} onHide={() => setShowTrace(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>Test run - AI trace</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {traceQuery.isLoading ? (
            <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
          ) : traceQuery.data ? (
            <AITraceViewer trace={traceQuery.data} />
          ) : (
            <div style={{ fontSize: 12, color: "#888" }}>
              No AI trace available for this run.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default LandlordTestPanel;
